import { useState, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetFeedbackQuestionsQuery, useSaveFeedbackAnswersMutation } from '../../../../api/endpoints/technicalSupportRequest';
import toastService from '../../../../utils/toastService';
import { useGetLookUpDropdownListQuery } from '../../../../api';

const RATING_OPTIONS = [1, 2, 3, 4, 5];

const COMMENT_QUESTION_ID = 5;

const FeedbackDialog = ({ visible, onHide, onSubmit, isSubmitting }) => {
    const { th, getCurrentLanguageId } = useLanguage();

    const { data: ratingLookupOptions = [] } = useGetLookUpDropdownListQuery({
        LanguageId: getCurrentLanguageId(),
        GroupCode: 'FEEDBACK_RATING'
    });

    const ratingLabels = useMemo(() => {
        const map = {};
        (ratingLookupOptions || []).forEach((item) => {
            const code = Number(item?.code ?? item?.lookupCode);
            const label = item?.translatedText || item?.text || String(code);
            if (Number.isFinite(code)) {
                map[code] = label;
            }
        });
        return map;
    }, [ratingLookupOptions]);

    const { data: questionsRaw, isLoading } = useGetFeedbackQuestionsQuery(getCurrentLanguageId(), { skip: !visible });

    const allQuestions = Array.isArray(questionsRaw) ? questionsRaw : [];

    // Rating soruları ve comment sorusunu ayır
    const ratingQuestions = allQuestions.filter((q) => q.id !== COMMENT_QUESTION_ID);
    const commentQuestion = allQuestions.find((q) => q.id === COMMENT_QUESTION_ID);

    const [ratings, setRatings] = useState({});
    const [comment, setComment] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const hasLowRating = useMemo(() => {
        return Object.values(ratings).some((r) => r === 1 || r === 2);
    }, [ratings]);

    const isCommentRequired = hasLowRating;

    const isValid = useMemo(() => {
        if (ratingQuestions.length === 0) return false;
        const allRated = ratingQuestions.every((q) => !!ratings[q.id]);
        if (!allRated) return false;
        if (isCommentRequired && !comment.trim()) return false;
        return true;
    }, [ratingQuestions, ratings, isCommentRequired, comment]);

    const handleSubmit = async () => {
        setSubmitted(true);
        if (!isValid) return;

        // Rating cevapları
        const answers = ratingQuestions.map((q) => ({
            questionId: q.id,
            rating: ratings[q.id],
            comment: null
        }));

        // Comment sorusunu ayrı kayıt olarak ekle
        if (commentQuestion) {
            answers.push({
                questionId: commentQuestion.id,
                rating: null,
                comment: comment.trim() || null
            });
        }

        await onSubmit(answers);
    };

    const footer = (
        <div className="flex justify-content-end">
            <Button label={th('feedback.submit')} icon="pi pi-check" onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting} />
        </div>
    );

    return (
        <Dialog visible={visible} onHide={onHide} header={th('feedback.title')} style={{ width: '850px' }} footer={footer} modal closable={false} className="p-fluid">
            {isLoading ? (
                <div className="flex justify-content-center p-4">
                    <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem' }} />
                </div>
            ) : (
                <div className="flex flex-column gap-3 pt-2">
                    {/* Rating tablosu */}
                    <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: 'var(--surface-100)' }}>
                                <th style={thStyle}>{th('feedback.columns.no')}</th>
                                <th style={thStyle}>{th('feedback.columns.criteria')}</th>
                                <th style={thStyle}>{th('feedback.columns.explanation')}</th>
                                {RATING_OPTIONS.map((r) => (
                                    <th key={r} style={{ ...thStyle, textAlign: 'center', width: '100px' }}>
                                        <span style={{ color: getRatingColor(r), fontWeight: 'bold' }}>{r}</span>
                                        <br />
                                        <span style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>{ratingLabels[r] || ''}</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {ratingQuestions.map((q, index) => (
                                <tr key={q.id} style={{ borderBottom: '1px solid var(--surface-border)' }}>
                                    <td style={tdStyle}>{index + 1}</td>
                                    <td style={{ ...tdStyle, fontWeight: '600' }}>{q.criteria}</td>
                                    <td style={{ ...tdStyle, color: 'var(--text-color-secondary)', fontSize: '0.875rem' }}>{q.explanation}</td>
                                    {RATING_OPTIONS.map((r) => (
                                        <td key={r} style={{ ...tdStyle, textAlign: 'center' }}>
                                            <div
                                                onClick={() => setRatings((prev) => ({ ...prev, [q.id]: r }))}
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    border: `2px solid ${getRatingColor(r)}`,
                                                    backgroundColor: ratings[q.id] === r ? getRatingColor(r) : 'transparent',
                                                    cursor: 'pointer',
                                                    margin: '0 auto',
                                                    transition: 'all 0.2s'
                                                }}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {submitted && ratingQuestions.some((q) => !ratings[q.id]) && <small className="p-error">{th('feedback.ratingRequired')}</small>}

                    {/* Genel yorum alanı — comment sorusunun criteria'sını başlık olarak kullan */}
                    <div className="mt-2">
                        <label className="font-bold block mb-2">
                            {commentQuestion?.criteria || th('feedback.comment')}
                            {isCommentRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {commentQuestion?.explanation && <p className="text-600 text-sm mb-2">{commentQuestion.explanation}</p>}
                        <InputTextarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={3}
                            className={submitted && isCommentRequired && !comment.trim() ? 'p-invalid w-full' : 'w-full'}
                            placeholder={isCommentRequired ? th('feedback.commentRequiredPlaceholder') : th('feedback.commentOptionalPlaceholder')}
                        />
                        {submitted && isCommentRequired && !comment.trim() && <small className="p-error">{th('feedback.commentRequired')}</small>}
                    </div>
                </div>
            )}
        </Dialog>
    );
};

const thStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    borderBottom: '2px solid var(--surface-border)',
    fontSize: '0.875rem'
};

const tdStyle = {
    padding: '10px 12px',
    verticalAlign: 'middle'
};

const getRatingColor = (rating) => {
    if (rating <= 2) return '#ef4444';
    if (rating === 3) return '#f59e0b';
    return '#22c55e';
};

export default FeedbackDialog;
